<template>
  <div class="container">
    <div v-if="loading" class="modern-loading-wrapper">
      <div class="modern-loader"></div>
      <div class="loading-text">Loading dealer group details...</div>
    </div>

    <div v-else>
      <!-- Group Details Section -->
      <div class="section-card">
        <div class="section-header-row">
          <div class="section-title-group">
            <h4 class="section-title mb-0">
              <i class="fa fa-info-circle text-dark mr-2"></i> Group Details
            </h4>
            <small class="section-purpose">Manage basic group information</small>
          </div>
          <div class="header-actions">
            <i 
              class="fa toggle-icon" 
              :class="isDetailsOpen ? 'fa-angle-up' : 'fa-angle-down'" 
              @click="isDetailsOpen = !isDetailsOpen"
              title="Expand/Collapse"
            ></i>
          </div>
        </div>
        <div v-show="isDetailsOpen" class="mt-3">
          <div class="form-group">
            <label>Group Name <span class="text-danger">*</span></label>
            <input 
              type="text" 
              v-model="dealerGroup.name" 
              class="form-control" 
              placeholder="Enter group name" 
              maxlength="100" 
            />
            <small class="text-muted">{{ dealerGroup.name.length }}/100 characters</small>
          </div>
          <div class="form-group mt-3">
            <label>Description</label>
            <textarea 
              v-model="dealerGroup.description" 
              class="form-control" 
              placeholder="Enter description" 
              maxlength="200" 
              rows="3"
            ></textarea>
            <small class="text-muted">{{ (dealerGroup.description || '').length }}/200 characters</small>
          </div>
          <div class="form-group mt-3">
            <label>Change Group Status:</label>
            <div class="toggle-switch-container">
              <label class="switch">
                <input type="checkbox" :checked="dealerGroup.is_active == 1" @change="toggleGroupStatus">
                <span class="slider round"></span>
              </label>
              <span class="status-text" :class="dealerGroup.is_active == 1 ? 'active' : 'inactive'">{{ dealerGroup.is_active == 1 ? 'Active' : 'Inactive' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Existing Dealers Section -->
      <div class="section-card">
        <div class="section-header-row">
          <div class="section-title-group">
            <h4 class="section-title mb-0">
              <i class="fa fa-users text-dark mr-2"></i> Existing Dealers
            </h4>
            <small class="section-purpose">Manage dealers currently assigned to this specific group.</small>
          </div>
          
          <div class="header-actions">
            <div class="search-wrapper" v-if="isExistingOpen">
              <input 
                type="text" 
                v-model="searchExistingQuery" 
                placeholder="Search existing..." 
                class="header-search-input"
              />
              <i class="fa fa-search search-icon"></i>
            </div>
            <i 
              class="fa toggle-icon" 
              :class="isExistingOpen ? 'fa-angle-up' : 'fa-angle-down'" 
              @click="isExistingOpen = !isExistingOpen"
              title="Expand/Collapse"
            ></i>
          </div>
        </div>
        
        <div v-show="isExistingOpen" class="table-responsive mt-3">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Dealer Details</th>
                <th class="text-center">Status</th>
                <th>City</th>
                <th class="text-center">Keep Assigned</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="dealer in filteredExistingList" :key="dealer.id">
                <td>
                  <div class="dealer-name">{{ dealer.business_name }}</div>
                  <div class="dealer-address">{{ dealer.address1 }}</div>
                </td>
                <td class="text-center">
                  <span :class="['badge', dealer.is_active == '1' ? 'badge-success' : 'badge-danger']">
                    {{ dealer.is_active == '1' ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td :title="dealer.city">{{ truncateText(dealer.city, 15) }}</td>
                <td class="text-center">
                  <input 
                    type="checkbox" 
                    class="big-checkbox"
                    :checked="isExistingSelected(dealer.id)"
                    @change="toggleExisting(dealer.id)"
                  />
                </td>
              </tr>
              <tr v-if="filteredExistingList.length === 0">
                <td colspan="4" class="text-center">
                  {{ searchExistingQuery ? 'No dealers found matching search.' : 'No existing dealers in this group.' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Swap Dealer Section -->
      <div class="section-card">
        <div class="section-header-row">
          <div class="section-title-group">
            <h4 class="section-title mb-0">
              <i class="fa fa-exchange text-dark mr-2"></i> Swap Dealers
            </h4>
            <small class="section-purpose">Transfer dealers from other existing groups into this one.</small>
          </div>
          
          <div class="header-actions">
            <div class="filter-controls" v-if="isSwapOpen">
              <!-- Filter Custom Menu -->
              <div class="custom-dropdown-container">
                <div 
                  class="icon-btn-wrapper" 
                  @click.stop="showSwapFilterMenu = !showSwapFilterMenu; showSwapSortMenu = false"
                  :class="{ 'active': showSwapFilterMenu }"
                  title="Filter by Group"
                >
                  <i class="fa fa-filter"></i>
                  <span v-if="swapFilterGroup" class="filter-badge"></span>
                </div>
                
                <div v-if="showSwapFilterMenu" class="custom-dropdown-menu">
                  <div class="dropdown-header">Filter by Group</div>
                  <div 
                    class="dropdown-item" 
                    :class="{ 'selected': swapFilterGroup === '' }"
                    @click="swapFilterGroup = ''; showSwapFilterMenu = false"
                  >
                    All Groups
                  </div>
                  <div 
                    v-for="grp in uniqueSwapGroups" 
                    :key="grp" 
                    class="dropdown-item"
                    :class="{ 'selected': swapFilterGroup === grp }"
                    @click="swapFilterGroup = grp; showSwapFilterMenu = false"
                  >
                    {{ grp }}
                  </div>
                </div>
              </div>
              
              <!-- Sort Custom Menu -->
              <div class="custom-dropdown-container">
                <div 
                  class="icon-btn-wrapper" 
                  @click.stop="showSwapSortMenu = !showSwapSortMenu; showSwapFilterMenu = false"
                  :class="{ 'active': showSwapSortMenu }"
                  title="Sort Dealers"
                >
                  <i class="fa fa-sort-amount-desc"></i>
                </div>
                
                <div v-if="showSwapSortMenu" class="custom-dropdown-menu">
                  <div class="dropdown-header">Sort Options</div>
                  <div 
                    class="dropdown-item" 
                    :class="{ 'selected': swapSortBy === 'group_asc' }"
                    @click="swapSortBy = 'group_asc'; showSwapSortMenu = false"
                  >
                    <i class="fa fa-sort-alpha-asc mr-2"></i> Group (A-Z)
                  </div>
                  <div 
                    class="dropdown-item" 
                    :class="{ 'selected': swapSortBy === 'group_desc' }"
                    @click="swapSortBy = 'group_desc'; showSwapSortMenu = false"
                  >
                    <i class="fa fa-sort-alpha-desc mr-2"></i> Group (Z-A)
                  </div>
                  <div class="dropdown-divider"></div>
                  <div 
                    class="dropdown-item" 
                    :class="{ 'selected': swapSortBy === 'name_asc' }"
                    @click="swapSortBy = 'name_asc'; showSwapSortMenu = false"
                  >
                    <i class="fa fa-font mr-2"></i> Name (A-Z)
                  </div>
                  <div 
                    class="dropdown-item" 
                    :class="{ 'selected': swapSortBy === 'name_desc' }"
                    @click="swapSortBy = 'name_desc'; showSwapSortMenu = false"
                  >
                    <i class="fa fa-font mr-2"></i> Name (Z-A)
                  </div>
                </div>
              </div>
            </div>

            <div class="search-wrapper" v-if="isSwapOpen">
              <input 
                type="text" 
                v-model="searchSwapQuery" 
                placeholder="Search dealers..." 
                class="header-search-input"
                @click.stop
              />
              <i class="fa fa-search search-icon"></i>
            </div>

            <i 
              class="fa toggle-icon" 
              :class="isSwapOpen ? 'fa-angle-up' : 'fa-angle-down'" 
              @click="isSwapOpen = !isSwapOpen"
              :title="isSwapOpen ? 'Collapse' : 'Expand'"
            ></i>
          </div>
        </div>
        
        <div v-show="isSwapOpen" class="table-responsive mt-3">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Dealer Details</th>
                <th class="text-center">Status</th>
                <th>City</th>
                <th>Existing Group</th>
                <th class="text-center danger-zone">Action (Danger Zone)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="dealer in filteredSwapList" :key="dealer.id">
                <td>
                  <div class="dealer-name">{{ dealer.business_name }}</div>
                  <div class="dealer-address">{{ dealer.address1 }}</div>
                </td>
                <td class="text-center">
                  <span :class="['badge', dealer.is_active == '1' ? 'badge-success' : 'badge-danger']">
                    {{ dealer.is_active == '1' ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td :title="dealer.city">{{ truncateText(dealer.city, 15) }}</td>
                <td :title="dealer.group_name"><span class="badge badge-info">{{ truncateText(dealer.group_name, 20) }}</span></td>
                <td class="text-center bg-light-danger">
                  <button 
                    type="button" 
                    class="btn btn-sm"
                    :class="isSwapped(dealer.id) ? 'btn-danger' : 'btn-success'"
                    @click="toggleSwap(dealer.id)"
                  >
                    {{ isSwapped(dealer.id) ? 'Unswap Group' : 'Swap Group' }}
                  </button>
                </td>
              </tr>
              <tr v-if="filteredSwapList.length === 0">
                <td colspan="5" class="text-center">
                  {{ searchSwapQuery ? 'No dealers found matching search.' : 'No dealers available to swap.' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Map New Dealer Section -->
      <div class="section-card">
        <div class="section-header-row">
          <div class="section-title-group">
            <h4 class="section-title mb-0">
              <i class="fa fa-user-plus text-dark mr-2"></i> Add Dealers
            </h4>
            <small class="section-purpose">Select and assign dealers who are currently unassigned.</small>
          </div>
          
          <div class="header-actions">
            <div class="search-wrapper" v-if="isMapOpen">
              <input 
                type="text" 
                v-model="searchMapQuery" 
                placeholder="Search new dealers..." 
                class="header-search-input"
              />
              <i class="fa fa-search search-icon"></i>
            </div>
            <i 
              class="fa toggle-icon" 
              :class="isMapOpen ? 'fa-angle-up' : 'fa-angle-down'" 
              @click="isMapOpen = !isMapOpen"
              title="Expand/Collapse"
            ></i>
          </div>
        </div>

        <div v-show="isMapOpen" class="table-responsive mt-3">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Dealer Details</th>
                <th class="text-center">Status</th>
                <th>City</th>
                <th class="text-center">Map (Select)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="dealer in filteredMapList" :key="dealer.id">
                <td>
                  <div class="dealer-name">{{ dealer.business_name }}</div>
                  <div class="dealer-address">{{ dealer.address1 }}</div>
                </td>
                <td class="text-center">
                  <span :class="['badge', dealer.is_active == '1' ? 'badge-success' : 'badge-danger']">
                    {{ dealer.is_active == '1' ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td :title="dealer.city">{{ truncateText(dealer.city, 15) }}</td>
                <td class="text-center">
                  <input 
                    type="checkbox" 
                    class="big-checkbox"
                    :checked="isMapped(dealer.id)"
                    @change="toggleMap(dealer.id)"
                  />
                </td>
              </tr>
              <tr v-if="filteredMapList.length === 0">
                <td colspan="4" class="text-center">
                  {{ searchMapQuery ? 'No dealers found matching search.' : 'No unassigned dealers available.' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Form Actions -->
      <div class="form-actions">
        <button class="btn btn-danger" @click="cancel">Cancel</button>
        <button class="btn btn-success" @click="updateDealerGroup" :disabled="updating">
          <span v-if="updating">
            <span class="spinner"></span>
          </span>
          <span v-else>
            Update Dealer Group
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import { advancedViewDealerGroup, editDealerGroup } from "@/services/dataService";

export default {
  name: "EditDealerGroup",
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      token: localStorage.getItem("token_set") || "",
      profileId: localStorage.getItem("profile_id") || "",
      reportingProfileId: localStorage.getItem("reportingL1ProfileId") || "",
      userCategory: localStorage.getItem("user_label") || "",
      loading: true,
      updating: false,
      dealerGroupId: this.$route.query.dealerGroupId,
      
      // Section Toggles
      isDetailsOpen: true,
      isExistingOpen: true,
      isSwapOpen: true,
      isMapOpen: true,
      
      // Search Queries
      searchExistingQuery: "",
      searchSwapQuery: "",
      searchMapQuery: "",
      
      // Swap Section Controls
      swapSortBy: 'group_asc',
      swapFilterGroup: '',
      showSwapSortMenu: false,
      showSwapFilterMenu: false,

      dealerGroup: {
        id: "",
        name: "",
        description: "",
        is_active: 1,
      },

      // Selected IDs Containers
      selectedExistingIds: [], 
      selectedSwapIds: [],
      selectedMapIds: [],  

      // Data Lists
      existingDealersList: [],
      swapDealersList: [],
      mapDealersList: []
    };
  },
  computed: {
    filteredExistingList() {
      if (!this.existingDealersList) return [];
      if (!this.searchExistingQuery) return this.existingDealersList;
      const query = this.searchExistingQuery.toLowerCase();
      return this.existingDealersList.filter(dealer => 
        (dealer.business_name || '').toLowerCase().includes(query) ||
        (dealer.city || '').toLowerCase().includes(query)
      );
    },
    uniqueSwapGroups() {
      if (!this.swapDealersList) return [];
      const groups = this.swapDealersList.map(d => d.group_name).filter(Boolean);
      return [...new Set(groups)].sort();
    },
    filteredSwapList() {
      if (!this.swapDealersList) return [];
      let list = [...this.swapDealersList];
      
      // Filter by search query
      if (this.searchSwapQuery) {
        const query = this.searchSwapQuery.toLowerCase();
        list = list.filter(dealer => 
          (dealer.business_name || '').toLowerCase().includes(query) ||
          (dealer.group_name || '').toLowerCase().includes(query) ||
          (dealer.city || '').toLowerCase().includes(query)
        );
      }

      // Filter by group
      if (this.swapFilterGroup) {
        list = list.filter(dealer => dealer.group_name === this.swapFilterGroup);
      }

      // Sort logic
      list.sort((a, b) => {
        let comparison = 0;
        if (this.swapSortBy.startsWith('group')) {
          comparison = (a.group_name || '').localeCompare(b.group_name || '');
          if (comparison === 0) {
            comparison = (a.business_name || '').localeCompare(b.business_name || '');
          }
          return this.swapSortBy.endsWith('desc') ? comparison * -1 : comparison;
        } else {
          comparison = (a.business_name || '').localeCompare(b.business_name || '');
          return this.swapSortBy.endsWith('desc') ? comparison * -1 : comparison;
        }
      });

      return list;
    },
    filteredMapList() {
      if (!this.mapDealersList) return [];
      if (!this.searchMapQuery) return this.mapDealersList;
      const query = this.searchMapQuery.toLowerCase();
      return this.mapDealersList.filter(dealer => 
        (dealer.business_name || '').toLowerCase().includes(query) ||
        (dealer.city || '').toLowerCase().includes(query)
      );
    }
  },
  mounted() {
    this.fetchGroupData();
    
    // Global click listener to close dropdowns
    window.addEventListener('click', () => {
      this.showSwapSortMenu = false;
      this.showSwapFilterMenu = false;
    });
  },
  methods: {
    truncateText(text, length) {
      if (!text) return '';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    },

    async fetchGroupData() {
      this.loading = true;
      
      try {
        const response = await advancedViewDealerGroup(this.dealerGroupId, this.token);
        
        if (response.data && response.data.status === 'success' && response.data.data) {
          const data = response.data.data;
          
          // 1. Group Details
          this.dealerGroup = {
            id: data.groupDetails.id,
            name: data.groupDetails.name,
            description: data.groupDetails.description,
            is_active: data.groupDetails.is_active,
          };

          // 2. Existing Dealers
          this.existingDealersList = data.groupDealers || [];
          this.selectedExistingIds = this.existingDealersList.map(d => d.id);

          // 3. Swap Dealers
          this.swapDealersList = data.otherGroupDealers || [];
          this.selectedSwapIds = [];

          // 4. Map Dealers
          this.mapDealersList = data.unassignedDealers || [];
          this.selectedMapIds = [];
          
        } else {
          this.toast.error(response.data.message || "Failed to load dealer group data.");
        }
      } catch (error) {
        console.error("Error fetching group data:", error);
        this.toast.error("Error loading dealer group data.");
      } finally {
        this.loading = false;
      }
    },

    toggleGroupStatus() {
      this.dealerGroup.is_active = this.dealerGroup.is_active == 1 ? "0" : "1";
    },

    isExistingSelected(id) {
      return this.selectedExistingIds.includes(id);
    },
    toggleExisting(id) {
      if (this.isExistingSelected(id)) {
        this.selectedExistingIds = this.selectedExistingIds.filter(item => item !== id);
      } else {
        this.selectedExistingIds.push(id);
      }
    },

    isSwapped(id) {
      return this.selectedSwapIds.includes(id);
    },
    toggleSwap(id) {
      if (this.isSwapped(id)) {
        this.selectedSwapIds = this.selectedSwapIds.filter(item => item !== id);
      } else {
        this.selectedSwapIds.push(id);
      }
    },

    isMapped(id) {
      return this.selectedMapIds.includes(id);
    },
    toggleMap(id) {
      if (this.isMapped(id)) {
        this.selectedMapIds = this.selectedMapIds.filter(item => item !== id);
      } else {
        this.selectedMapIds.push(id);
      }
    },

    toggleSwapSort() {
      // No longer used, handled by select
    },
    cancel() {
      this.$router.back(); 
    },

    validateForm() {
      if (!this.dealerGroup.name || this.dealerGroup.name.trim() === '') {
        this.toast.error("Group name is required.");
        return false;
      }
      return true;
    },

    async updateDealerGroup() {
      if (!this.validateForm()) return;

      this.updating = true;

      // Calculate removal list (dealers that were in the group but are now unchecked)
      const removeList = this.existingDealersList
        .filter(d => !this.selectedExistingIds.includes(d.id))
        .map(d => d.id);

      const payload = {
        "id": this.dealerGroup.id,
        "name": this.dealerGroup.name,
        "description": this.dealerGroup.description,
        "is_active": this.dealerGroup.is_active,
        "distributor_id": this.userCategory === 'DISTRIBUTOR BACK OFFICE' ? this.reportingProfileId : this.profileId,
        "updated_by": this.profileId,
        "remove-dealer-group": removeList,
        "swap-dealer-group": this.selectedSwapIds,
        "add-dealer-group": this.selectedMapIds
      };

      console.log("Update Dealer Group Payload:", JSON.stringify(payload, null, 2));

      try {
        const response = await editDealerGroup(payload, this.token);
        if (response.data && response.data.status === 'success') {
          this.toast.success(response.data.message || "Dealer Group updated successfully!");
          this.$router.push('/dealer-groups');
        } else {
          this.toast.error(response.data.message || "Failed to update dealer group.");
        }
      } catch (error) {
        console.error("Error updating dealer group:", error);
        this.toast.error("An error occurred while updating.");
      } finally {
        this.updating = false;
      }
    }
  }
};
</script>

<style scoped>
html, body {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #f0f0f0;
}

.container {
  background-color: #ffffff;
  padding: 20px 15px;
  margin: 10px auto;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  box-sizing: border-box;
  max-width: 100%;
}

.section-card {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 20px;
  background-color: #fff;
}

.section-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  margin-bottom: 10px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  text-transform: uppercase;
  display: flex;
  align-items: center;
}

.mr-2 {
  margin-right: 12px !important;
}

.section-title-group {
  display: flex;
  flex-direction: column;
}

.section-purpose {
  font-size: 12px;
  color: #777;
  font-weight: 400;
  margin-left: 0;
  text-transform: none;
  margin-top: 4px;
}

.custom-dropdown-container {
  position: relative;
}

.icon-btn-wrapper {
  position: relative;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.2s;
  color: #555;
}

.icon-btn-wrapper:hover, .icon-btn-wrapper.active {
  background-color: #f0f0f0;
  border-color: #007bff;
  color: #007bff;
}

.filter-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 6px;
  height: 6px;
  background-color: #007bff;
  border-radius: 50%;
}

.custom-dropdown-menu {
  position: absolute;
  top: 38px;
  right: 0;
  min-width: 180px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 1000;
  padding: 5px 0;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
}

.dropdown-header {
  padding: 8px 15px;
  font-size: 11px;
  text-transform: uppercase;
  color: #999;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.dropdown-item {
  padding: 8px 15px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background 0.2s;
}

.dropdown-item:hover {
  background-color: #f5f8ff;
  color: #007bff;
}

.dropdown-item.selected {
  background-color: #eef4ff;
  color: #007bff;
  font-weight: 600;
}

.dropdown-divider {
  height: 1px;
  background-color: #eee;
  margin: 5px 0;
}

.filter-controls {
  display: flex;
  gap: 8px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 15px; 
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.header-search-input {
  padding: 5px 30px 5px 10px; 
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  width: 200px;
  transition: border-color 0.3s;
}

.header-search-input:focus {
  border-color: #007bff;
  outline: none;
}

.search-icon {
  position: absolute;
  right: 10px;
  color: #999;
  font-size: 13px;
  pointer-events: none; 
}

.toggle-icon {
  cursor: pointer;
  color: #6c757d;
  font-size: 1.1rem;
  transition: color 0.3s ease;
  padding: 5px;
}

.toggle-icon:hover { 
  color: #007bff; transform: scale(1.1); 
}

.status-text {
  color: #6c757d !important;
  font-weight: 600;
}

.status-text.active {
  color: #28a745 !important;
}

.status-text.inactive {
  color: #dc3545 !important;
}

.mb-0 { 
  margin-bottom: 0 !important; 
}

.mt-3 { 
  margin-top: 1rem !important; 
}

.row {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.col-md-6 {
  flex: 1 1 45%; 
  min-width: 300px;
}

.form-group { 
  margin-bottom: 10px; 
}

label {
  display: block;
  font-weight: 500;
  margin-bottom: 5px;
  color: #555;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box; 
}

.text-danger { 
  color: #dc3545; 
}

.table-responsive { 
  overflow-x: auto; 
}

.table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 10px;
}

.table th, .table td {
  padding: 10px;
  border: 1px solid #ddd;
  text-align: left;
}

.table th { 
  background-color: #f8f9fa; 
  font-weight: 600; 
  white-space: nowrap;
}

.text-center { 
  text-align: center !important; 
}

.big-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.badge-info {
  background-color: #17a2b8;
  color: white;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 12px;
}

.badge-success {
  background-color: #28a745;
  color: white;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.badge-danger {
  background-color: #dc3545;
  color: white;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.dealer-name {
  font-weight: 600;
  color: #333;
  font-size: 13px;
}

.dealer-address {
  font-size: 11px;
  color: #777;
  margin-top: 2px;
}

.danger-zone {
  background-color: #f8d7da !important;
  color: #721c24;
  border-color: #f5c6cb;
}

.bg-light-danger { 
  background-color: #fff5f5; 
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-sm {
  padding: 5px 10px;
  font-size: 12px;
}

.btn-success { 
  background-color: #28a745; 
  color: white; 
}

.btn-success:hover { 
  background-color: #218838; 
}

.btn-danger { 
  background-color: #dc3545; 
  color: white; 
}

.btn-danger:hover { 
  background-color: #c82333; 
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 20px;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #54ba4a;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
  display: inline-block;
  vertical-align: middle;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.modern-loading-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  width: 100%;
}

.modern-loader {
  width: 48px;
  height: 48px;
  border: 3px solid #eef2f7;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  animation: modern-spin 1s cubic-bezier(0.5, 0.1, 0.4, 0.9) infinite;
}

.loading-text {
  margin-top: 20px;
  font-size: 15px;
  font-weight: 500;
  color: #666;
  letter-spacing: 0.3px;
  animation: modern-pulse 1.5s ease-in-out infinite;
}

@keyframes modern-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes modern-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.toggle-switch-container {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 38px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.switch input { 
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: .4s;
}

input:checked + .slider {
  background-color: #28a745;
}

input:focus + .slider {
  box-shadow: 0 0 1px #28a745;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}
</style>
